import {
  ColDef,
  ICellRendererParams,
  ISetFilterParams,
  ITooltipParams,
} from '@ag-grid-community/core'
import {
  generateMdiIcon,
  HeliosColumnTypes,
  heliosColumnTypes,
  heliosDefaultColWidth,
  HeliosMdiIconClass,
  HeliosMdiIconModifierClass,
  IssueStatusName,
  SELECT_ALL,
} from '@helios/ag-grid-config'
import { BreakPriority } from '../model/break.model'

export const priorityIconTemplate = (params: ICellRendererParams) => {
  if (params.value === '' || params.value == null) {
    return ''
  }

  // Handle both object (from valueGetter) and direct priority value
  const priority =
    typeof params.value === 'object' ? params.value.priority : params.value
  const isRestrictedBreak =
    typeof params.value === 'object'
      ? !!params.value.restrictedAction
      : !!params.data?.restrictedAction
  const isUnverified =
    typeof params.value === 'object'
      ? params.value.unverified ?? false
      : params.data?.metaData.Unverified ?? false
  const status =
    typeof params.value === 'object'
      ? params.value.statusName
      : (params.data?.statusName as IssueStatusName)

  const iconClass = getPriorityIconClass(priority)
  const colorClass = getPriorityColorClass(
    priority,
    status,
    isUnverified,
    isRestrictedBreak,
  )

  return generateMdiIcon(iconClass, colorClass ? [colorClass] : [])
}

/**
 * Get icon class of a priority.
 */
function getPriorityIconClass(priority: BreakPriority): HeliosMdiIconClass {
  switch (priority) {
    case BreakPriority.HIGH:
      return 'mdi-alert-circle'
    case BreakPriority.MEDIUM:
      return 'mdi-alert'
    default:
      return 'mdi-help-circle'
  }
}

/**
 * Get the color of a priority icon.
 */
function getPriorityColorClass(
  priority: BreakPriority,
  status: string,
  isUnverified: boolean,
  isRestrictedBreak: boolean,
): HeliosMdiIconModifierClass | undefined {
  if (status === 'IGNORED') {
    return 'text-muted'
  }
  switch (priority) {
    case BreakPriority.HIGH:
      return isUnverified
        ? 'text-muted'
        : isRestrictedBreak
        ? 'text-purple'
        : 'text-danger'
    case BreakPriority.MEDIUM:
      return isUnverified
        ? 'text-muted'
        : isRestrictedBreak
        ? 'text-purple'
        : 'text-warning'
    default:
      return
  }
}

/**
 * Format the casing of the priority names
 */
export function issuePriorityToDisplayName(priority: BreakPriority) {
  switch (priority) {
    case BreakPriority.HIGH:
      return 'High'
    case BreakPriority.MEDIUM:
      return 'Medium'
    default:
      return
  }
}

/**
 * Get the tooltip to display name of a priority
 */
function tooltipValueGetter(params: ITooltipParams) {
  return params.data?.metaData.Unverified
    ? 'Unverified'
    : issuePriorityToDisplayName(params.value as BreakPriority) ?? ''
}

/**
 * Format the break priority filter
 */
export function breakPriorityFilterCellRenderer(params: ICellRendererParams) {
  if (params.value === SELECT_ALL) {
    return SELECT_ALL
  }

  const priority = params.value as BreakPriority

  const icon = priorityIconTemplate(params)
  const description = issuePriorityToDisplayName(priority)

  return `${icon} ${description}`
}

export const sortPriorityFilterValues = (
  a: BreakPriority,
  b: BreakPriority,
) => {
  const orderedPriorityArray = [BreakPriority.HIGH, BreakPriority.MEDIUM]
  if (a === b) return 0
  return orderedPriorityArray.indexOf(a) > orderedPriorityArray.indexOf(b)
    ? 1
    : -1
}

export const HsBreakPriorityColDef: ColDef = {
  cellStyle: { textAlign: 'center' },
  width: heliosDefaultColWidth.MediumIcon,
  filter: 'agSetColumnFilter',
  cellRenderer: priorityIconTemplate,
  tooltipValueGetter: tooltipValueGetter,
  filterParams: {
    defaultToNothingSelected: true,
    closeOnApply: true,
    cellRenderer: breakPriorityFilterCellRenderer,
    values: [BreakPriority.HIGH, BreakPriority.MEDIUM],
    comparator: sortPriorityFilterValues,
    buttons: ['apply', 'reset'],
  } as ISetFilterParams,
}

export enum BreaksPanelColumnTypeEnum {
  BreakPriorityColumn = 'BreakPriority',
}

export type BreaksPanelColumnTypes =
  | HeliosColumnTypes
  | BreaksPanelColumnTypeEnum

export const breakColumnTypes: Record<BreaksPanelColumnTypes | string, ColDef> =
  {
    ...heliosColumnTypes,
    [BreaksPanelColumnTypeEnum.BreakPriorityColumn]: HsBreakPriorityColDef,
  }
